import { ChevronLeft, ChevronRight } from 'lucide-react';
import { cn } from './cn';

export interface PaginationProps {
  page: number;
  pageSize: number;
  total: number;
  onPageChange: (page: number) => void;
  className?: string;
}

export function Pagination({ page, pageSize, total, onPageChange, className }: PaginationProps) {
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  const rangeStart = Math.max(1, Math.min(page - 2, totalPages - 4));
  const rangeEnd = Math.min(totalPages, rangeStart + 4);
  const pages: number[] = [];
  for (let i = rangeStart; i <= rangeEnd; i++) pages.push(i);

  const from = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const to = Math.min(page * pageSize, total);

  if (totalPages <= 1 && total <= pageSize) return null;

  return (
    <div className={cn('flex items-center justify-between border-t border-border-default pt-3 mt-1', className)}>
      <span className="select-none text-[11px] tabular-nums text-text-tertiary">
        {total === 0 ? 'No results' : `${from}–${to} of ${total.toLocaleString()}`}
      </span>

      <div className="flex items-center gap-0.5">
        <button
          disabled={page <= 1}
          onClick={() => onPageChange(page - 1)}
          aria-label="Previous page"
          className={cn(
            'flex h-6 w-6 items-center justify-center rounded transition-colors',
            page <= 1
              ? 'cursor-not-allowed opacity-30 text-text-tertiary'
              : 'text-text-secondary hover:bg-surface-1 hover:text-text-primary',
          )}
        >
          <ChevronLeft className="h-3.5 w-3.5" />
        </button>

        {rangeStart > 1 && (
          <>
            <button
              onClick={() => onPageChange(1)}
              className="flex h-6 min-w-[24px] items-center justify-center rounded px-1.5 text-[11px] tabular-nums text-text-secondary hover:bg-surface-1 hover:text-text-primary transition-colors"
            >
              1
            </button>
            {rangeStart > 2 && <span className="px-1 text-[11px] text-text-tertiary select-none">…</span>}
          </>
        )}

        {pages.map((p) => (
          <button
            key={p}
            onClick={() => onPageChange(p)}
            className={cn(
              'flex h-6 min-w-[24px] items-center justify-center rounded px-1.5 text-[11px] tabular-nums transition-colors',
              p === page
                ? 'bg-accent/15 font-semibold text-accent'
                : 'text-text-secondary hover:bg-surface-1 hover:text-text-primary',
            )}
          >
            {p}
          </button>
        ))}

        {rangeEnd < totalPages && (
          <>
            {rangeEnd < totalPages - 1 && <span className="px-1 text-[11px] text-text-tertiary select-none">…</span>}
            <button
              onClick={() => onPageChange(totalPages)}
              className="flex h-6 min-w-[24px] items-center justify-center rounded px-1.5 text-[11px] tabular-nums text-text-secondary hover:bg-surface-1 hover:text-text-primary transition-colors"
            >
              {totalPages}
            </button>
          </>
        )}

        <button
          disabled={page >= totalPages}
          onClick={() => onPageChange(page + 1)}
          aria-label="Next page"
          className={cn(
            'flex h-6 w-6 items-center justify-center rounded transition-colors',
            page >= totalPages
              ? 'cursor-not-allowed opacity-30 text-text-tertiary'
              : 'text-text-secondary hover:bg-surface-1 hover:text-text-primary',
          )}
        >
          <ChevronRight className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  );
}
